import React from "react";

/**
 * Shared form primitives for tool cards.
 *
 * The old cards each styled themselves with raw utility classes, so they drifted
 * apart -- different paddings, different button colours, different ways of
 * reporting an error. These wrap the design tokens so a card describes what it
 * needs rather than how it looks, and restyling happens in one place.
 *
 * Deliberately small: these are conveniences, not a component framework. A card
 * with an unusual need should drop to plain markup rather than bend a primitive
 * out of shape.
 */

/** Label + control + optional hint. The building block for everything below. */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <div className="mt-1">{children}</div>
      {error ? (
        <span className="block text-[12px] text-red-600 mt-1">{error}</span>
      ) : hint ? (
        <span className="block text-ink-faint text-[12px] mt-1">{hint}</span>
      ) : null}
    </label>
  );
}

const INPUT =
  "w-full bg-card border border-line-strong rounded-sm2 px-3 py-1.5 text-sm " +
  "focus:outline-none focus:ring-2 focus:ring-brand-tint disabled:opacity-50";

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${INPUT} ${props.className ?? ""}`} />;
}

export function DateInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input type="date" {...props} className={`${INPUT} ${props.className ?? ""}`} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${INPUT} ${props.className ?? ""}`} />;
}

/** File picker styled to match the rest of the form controls. */
export function FileInput({
  onFile,
  accept,
  file,
  disabled,
}: {
  onFile: (f: File | null) => void;
  accept?: string;
  file?: File | null;
  disabled?: boolean;
}) {
  return (
    <div>
      {/* Boxed like the text inputs so it reads as a field, with the button
          itself tinted -- picking a file is usually the main action on these
          cards, and the muted grey pill did not look clickable. */}
      <div
        className={`flex items-center rounded-sm2 border border-line-strong bg-card px-2 py-1.5
                    focus-within:ring-2 focus-within:ring-brand-tint ${
                      disabled ? "opacity-50" : ""
                    }`}
      >
        <input
          type="file"
          accept={accept}
          disabled={disabled}
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm text-ink-soft
                     file:mr-3 file:py-1.5 file:px-4 file:rounded-sm2 file:border-0
                     file:bg-brand file:text-white file:font-semibold file:text-[13px]
                     file:cursor-pointer hover:file:bg-brand-deep file:transition"
        />
      </div>
      {file && (
        <span className="block text-[12px] text-brand-deep mt-1 truncate">
          Selected: {file.name}
        </span>
      )}
    </div>
  );
}

/** Pill-style segmented control. Used for source and output switches. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  disabled,
}: {
  value: T;
  onChange: (v: T) => void;
  options: [T, string][];
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex bg-paper-2 rounded-sm2 p-0.5 text-[13px] font-semibold">
      {options.map(([val, label]) => (
        <button
          key={val}
          type="button"
          disabled={disabled}
          onClick={() => onChange(val)}
          className={`px-3 py-1 rounded-sm2 transition disabled:opacity-50 ${
            value === val ? "bg-card shadow-sm text-ink" : "text-ink-faint hover:text-ink-soft"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * Inline busy spinner.
 *
 * Lives in the button rather than as an overlay: an overlay hides the form you
 * just filled in, and on a job that runs for minutes people want to re-read
 * what they submitted. Marked aria-hidden because the button text already
 * announces the state.
 */
export function Spinner({ className = "" }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block h-3.5 w-3.5 rounded-full border-2 border-current border-r-transparent animate-spin align-[-2px] ${className}`}
    />
  );
}

/** Right-aligned action row. */
export function Actions({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-2 flex-wrap">{children}</div>;
}

export function PrimaryButton({
  busy,
  busyLabel,
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean; busyLabel?: string }) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || busy}
      className={`btn-primary ${rest.className ?? ""}`}
    >
      {busy ? (
        <span className="inline-flex items-center gap-2">
          <Spinner /> {busyLabel ?? "Working…"}
        </span>
      ) : (
        children
      )}
    </button>
  );
}

export function GhostButton({
  busy,
  busyLabel,
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean; busyLabel?: string }) {
  return (
    <button {...rest} disabled={rest.disabled || busy} className="btn-ghost">
      {busy ? (
        <span className="inline-flex items-center gap-2">
          <Spinner /> {busyLabel ?? "Working…"}
        </span>
      ) : (
        children
      )}
    </button>
  );
}

/**
 * Success/failure banner.
 *
 * Takes the shape these endpoints actually return -- {status, message} or
 * {error} -- so cards stop hand-rolling three different error renderings.
 */
export function ResultBanner({ result }: { result: any }) {
  if (!result) return null;

  const failed =
    result.status === "failure" || result.status === "error" || Boolean(result.error);
  const message =
    result.message ??
    result.error ??
    (failed ? "Something went wrong." : "Done.");

  return (
    <div
      role="status"
      className={`rounded-sm2 p-3 text-sm animate-[fadeIn_.2s_ease-out_both] ${
        failed
          ? "bg-red-50 border border-red-200 text-red-800"
          : "bg-brand-tint text-brand-deep"
      }`}
    >
      <div className={failed ? "font-semibold" : ""}>{message}</div>
      {result.details && (
        <div className="text-[12px] mt-1 opacity-80 font-mono break-all">{result.details}</div>
      )}
    </div>
  );
}

/**
 * Indeterminate progress bar for long operations.
 *
 * A spinner alone starts to read as "hung" after thirty seconds or so. This
 * sweeps continuously, which says "still working" without pretending to know a
 * percentage we cannot measure.
 */
export function ProgressBar() {
  return (
    <div className="h-1 w-full bg-paper-2 rounded-full overflow-hidden" role="progressbar" aria-label="Working">
      <div className="h-full w-1/3 bg-brand rounded-full animate-[sweep_1.4s_ease-in-out_infinite]" />
    </div>
  );
}

/** Note explaining that an operation is slow, shown while it runs. */
export function BusyNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-ink-soft text-[13px] animate-[fadeIn_.2s_ease-out_both]">{children}</p>
  );
}

/** Intro line under a tool's title. */
export function CardIntro({ children }: { children: React.ReactNode }) {
  return <p className="text-ink-soft text-[13px]">{children}</p>;
}
